import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { unwrapResult } from '@reduxjs/toolkit';
import { SelectChangeEvent } from '@mui/material';

import { Badge, CustomAlert, CustomButton, CustomSelect, Text } from '../../../../../shared/uiComponents';
import { errorNormalizer } from '../../../../../shared/Helpers/functions';
import { PEARL, RED } from '../../../../../shared/Helpers/colors';
import { Calendar, UserTie } from '../../../../../shared/Assets/menuIcons';
import { History, TrashEmpty } from '../../../../../shared/Assets/sessionDetailsSVGs';
import { ButtonWrapper } from '../../../../../components/StyledComponents';
import { useFetchDTTTargetHistory, useFetchTATargetHistory } from './hooks';

import { DispatchProperties, useSelector } from '../../../../../redux/store';
import {
	deletePrompts,
	updateDTTPrompts,
	updateTAPrompts,
} from '../../../../../redux/State/clientSlice/dataCollectionSlice';
import {
	DTTPromptsResponseProperties,
	TAPromptsResponseProperties,
} from '../../../../../redux/API/ClientAPIHelpers/calendarProperties';
import { UpdatePromptsProperties } from '../../../../../redux/API/ClientAPIHelpers/dataCollectionProperties';

const DTTContent = ({
	targetId,
	targetName,
	item,
	fetchHistory,
}: {
	fetchHistory: () => void;
	item: DTTPromptsResponseProperties;
	targetId: string;
	targetName: string;
}) => {
	const dispatch = useDispatch<DispatchProperties>();

	const [areYouSure, setAreYouSure] = useState<boolean>(false);
	const [promptCodeId, setPromptCodeId] = useState<string>(item.promptCode.id);

	const loading = useSelector((state) => state.dataCollection.loading);
	const promptsData = useSelector((state) => state.dataCollection.promptCodes);

	const onPromptUpdate = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;

		if (!value) return;
		setPromptCodeId(value);
		const data: UpdatePromptsProperties = {
			targetId,
			data: [{ promptCodeId: value, responseId: item.id }],
		};
		dispatch(updateDTTPrompts(data))
			.then(unwrapResult)
			.then(() => {
				fetchHistory();
				toast('Success');
			})
			.catch(errorNormalizer);
	};

	const onDeleteHandler = () => {
		dispatch(deletePrompts([item.id]))
			.then(unwrapResult)
			.then(() => {
				fetchHistory();
				toast('Deleted');
			})
			.catch(errorNormalizer)
			.finally(() => setAreYouSure(false));
	};

	return (
		<div className="historyCard">
			<div className="content">
				<Text title={targetName} type="psm" fontWeight="medium" />
				<div className="badges">
					<Badge label={item.user.fullName} type="gray" sizeType="sm" startIcon={<UserTie color={PEARL.BASE[500]} />} />
					<Badge
						label={dayjs(item.date).format('MM/DD/YYYY : hh:mm A')}
						type="gray"
						sizeType="sm"
						startIcon={<Calendar color={PEARL.BASE[500]} />}
					/>
				</div>
			</div>
			<div className="historyActions">
				<CustomSelect data={promptsData} value={promptCodeId} setValue={onPromptUpdate} disabled={loading} />
				<CustomButton
					iconOnly={<TrashEmpty color={RED.BASE} />}
					onClick={() => setAreYouSure(true)}
					buttonType="secondary"
					style={{ backgroundColor: RED.QUATERNARY, borderColor: RED.BASE }}
					sizeType="md"
				/>
				<CustomAlert
					open={areYouSure}
					onClose={() => setAreYouSure(false)}
					title="Are you sure?"
					Content={() => (
						<ButtonWrapper>
							<CustomButton title="Delete" onClick={onDeleteHandler} loading={loading} />
							<CustomButton title="Cancel" onClick={() => setAreYouSure(false)} buttonType="secondaryGray" />
						</ButtonWrapper>
					)}
				/>
			</div>
		</div>
	);
};

const TAContent = ({
	targetId,
	item,
	fetchHistory,
}: {
	fetchHistory: () => void;
	item: TAPromptsResponseProperties;
	targetId: string;
}) => {
	const dispatch = useDispatch<DispatchProperties>();

	const [areYouSure, setAreYouSure] = useState<boolean>(false);
	const [promptCodeId, setPromptCodeId] = useState<string>(item.promptCode.id);

	const promptsData = useSelector((state) => state.dataCollection.promptCodes);
	const loading = useSelector((state) => state.dataCollection.loading);

	const onPromptUpdate = (event: SelectChangeEvent<string>) => {
		const { value } = event.target;

		if (!value) return;
		setPromptCodeId(value);
		const data: UpdatePromptsProperties = {
			targetId,
			data: [{ promptCodeId: value, responseId: item.id }],
		};
		dispatch(updateTAPrompts(data))
			.then(unwrapResult)
			.then(() => {
				fetchHistory();
				toast('Success');
			})
			.catch(errorNormalizer);
	};

	const onDeleteHandler = () => {
		dispatch(deletePrompts([item.id]))
			.then(unwrapResult)
			.then(() => {
				fetchHistory();
				toast('Deleted');
			})
			.catch(errorNormalizer)
			.finally(() => setAreYouSure(false));
	};

	return (
		<div className="historyCard">
			<div className="content">
				<Text title={item.step.name} type="psm" fontWeight="medium" />
				<div className="badges">
					<Badge label={item.user.fullName} type="gray" sizeType="sm" startIcon={<UserTie color={PEARL.BASE[500]} />} />
					<Badge
						label={dayjs(item.date).format('MM/DD/YYYY : hh:mm A')}
						type="gray"
						sizeType="sm"
						startIcon={<Calendar color={PEARL.BASE[500]} />}
					/>
				</div>
			</div>
			<div className="historyActions">
				<CustomSelect data={promptsData} value={promptCodeId} setValue={onPromptUpdate} disabled={loading} />
				<CustomButton
					iconOnly={<TrashEmpty color={RED.BASE} />}
					onClick={() => setAreYouSure(true)}
					buttonType="secondary"
					style={{ backgroundColor: RED.QUATERNARY, borderColor: RED.BASE }}
					sizeType="md"
				/>
				<CustomAlert
					open={areYouSure}
					onClose={() => setAreYouSure(false)}
					title="Are you sure?"
					Content={() => (
						<ButtonWrapper>
							<CustomButton title="Delete" onClick={onDeleteHandler} loading={loading} />
							<CustomButton title="Cancel" onClick={() => setAreYouSure(false)} buttonType="secondaryGray" />
						</ButtonWrapper>
					)}
				/>
			</div>
		</div>
	);
};

export const DTTTargetHistory = ({
	targetId,
	targetName,
	fetchData,
}: {
	fetchData: () => void;
	targetId: string;
	targetName: string;
}) => {
	const [historyOpen, setHistoryOpen] = useState<boolean>(false);
	const { targetHistory, fetchHistory } = useFetchDTTTargetHistory(targetId);

	const closeHandler = useCallback(() => {
		fetchData();
		setHistoryOpen(false);
	}, [fetchData]);

	useEffect(() => {
		fetchHistory();
	}, [fetchHistory]);

	return (
		<>
			<CustomButton
				title="History"
				buttonType="secondary"
				startIcon={<History />}
				onClick={() => setHistoryOpen(true)}
			/>
			<CustomAlert
				open={historyOpen}
				onClose={closeHandler}
				title="History"
				titleOnTop={false}
				Icon={History}
				Content={() => (
					<>
						{targetHistory.length ? (
							targetHistory.map((x, index) => (
								<DTTContent
									key={index}
									targetId={targetId}
									item={x}
									fetchHistory={fetchHistory}
									targetName={targetName}
								/>
							))
						) : (
							<Text title="Please close history to refresh data" type="psm" fontWeight="medium" />
						)}
					</>
				)}
			/>
		</>
	);
};

export const TATargetHistory = ({ targetId, fetchData }: { fetchData: () => void; targetId: string }) => {
	const [historyOpen, setHistoryOpen] = useState<boolean>(false);
	const { targetHistory, fetchHistory } = useFetchTATargetHistory(targetId);

	const closeHandler = useCallback(() => {
		fetchData();
		setHistoryOpen(false);
	}, [fetchData]);

	useEffect(() => {
		fetchHistory();
	}, [fetchHistory]);

	return (
		<>
			<CustomButton
				title="History"
				buttonType="secondary"
				startIcon={<History />}
				onClick={() => setHistoryOpen(true)}
			/>
			<CustomAlert
				open={historyOpen}
				onClose={closeHandler}
				title="History"
				titleOnTop={false}
				Icon={History}
				Content={() => (
					<>
						{targetHistory.length ? (
							targetHistory.map((x, index) => (
								<TAContent key={index} targetId={targetId} item={x} fetchHistory={fetchHistory} />
							))
						) : (
							<Text title="Please close history to refresh data" type="psm" fontWeight="medium" />
						)}
					</>
				)}
			/>
		</>
	);
};
